import { Autocomplete, TextField, Checkbox, Chip, AutocompleteProps, CircularProgress } from '@mui/material';
import { getOptionLabelDependingOnLabelKeys } from '../../utils/componentUtils';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Fragment, SyntheticEvent } from 'react';
import { AutocompleteValue } from '@mui/material/useAutocomplete/useAutocomplete';

export type CustomAutoCompleteOption = {
    id: number | string;
    disabled?: boolean;
};

export type CustomAutoCompleteProps<
    T extends CustomAutoCompleteOption,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false
> = {
    options: T[];
    onChange: (value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>) => void;
    value: any;
    label: string;
    labelKeys: string[];
    loading?: boolean;
    isMultiSelect?: Multiple;
    error?: string;
} & Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'multiple' | 'options' | 'onChange' | 'value' | 'label' | 'labelKeys' | 'renderInput'
>;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function CustomAutoComplete<
    T extends CustomAutoCompleteOption,
    Multiple extends boolean | undefined = true,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false
>({
    options,
    onChange,
    value,
    label,
    labelKeys,
    isMultiSelect = true,
    loading,
    error,
    ...props
}: CustomAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
    const multiple = (isMultiSelect !== undefined ? isMultiSelect : true) as Multiple;

    const handleChange = (
        event: SyntheticEvent<Element, Event>,
        value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
    ) => {
        onChange(value);
    };

    return (
        <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
            multiple={multiple}
            options={options}
            getOptionLabel={(option) => option && getOptionLabelDependingOnLabelKeys(option, labelKeys)}
            value={value}
            onChange={handleChange}
            renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                    {isMultiSelect && <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />}
                    <span className="notranslate">{getOptionLabelDependingOnLabelKeys(option, labelKeys)}</span>
                </li>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getOptionLabelDependingOnLabelKeys(option, labelKeys)}
                        className="notranslate"
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    error={!!error}
                    helperText={error}
                    // color="error"
                    // focused
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
            loading={loading}
            disableCloseOnSelect={isMultiSelect}
            getOptionDisabled={(option: T) => !!option.disabled}
            {...props}
            noOptionsText="Keine Optionen"
        />
    );
}

export default CustomAutoComplete;
